import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DestinationAPI, FloorAPI, NavigationAPI, ShapeAPI, resolveAsset } from "../../../services/api";
import LeafletFloorMap from "../../../components/LeafletFloorMap";
import { parseNames } from "../../../utils/nameParser";
import { ReactComponent as ClickIcon } from "../../../assets/icons/adddestination/click.svg";
import { ReactComponent as EyeIcon } from "../../../assets/icons/adddestination/eye.svg";
import { ReactComponent as HandIcon } from "../../../assets/icons/adddestination/hand.svg";
import { ReactComponent as HomeIcon } from "../../../assets/icons/adddestination/home.svg";
import { ReactComponent as LocationIcon } from "../../../assets/icons/adddestination/location.svg";
import { ReactComponent as PlusIcon } from "../../../assets/icons/adddestination/plus.svg";
import { ReactComponent as UploadIcon } from "../../../assets/icons/adddestination/upload.svg";

function getFloorLabel(f, t) {
  const name = f.name || "";
  const level = f.level;
  if (!name) {
    return t("admin.navigation.floorLevel", { defaultValue: `ชั้น ${level}`, level });
  }
  const lowerName = name.toLowerCase().trim();
  const isGeneric =
    lowerName === `floor${level}` ||
    lowerName === `floor ${level}` ||
    lowerName === `floor_${level}` ||
    lowerName === `ชั้น${level}` ||
    lowerName === `ชั้น ${level}` ||
    lowerName === `ชั้นที่${level}` ||
    lowerName === `ชั้นที่ ${level}`;
  if (isGeneric) {
    return t("admin.navigation.floorLevel", { defaultValue: `ชั้น ${level}`, level });
  }
  return name;
}

const defaultPoints = [
  {
    id: "ID#QR-001",
    x: 38,
    y: 60,
    text: "ประชาสัมพันธ์...",
    img: "https://www.cmhy.city/uploads/photos/46-Health/226-Soundok-Hospital/17893-Table-Tennis-Training-Center--Chiang-Mai-Municipal-Stadium/place17893_IMG_20190528_1417378.jpg",
  },
  {
    id: "ID#QR-002",
    x: 42,
    y: 28,
    text: "ห้องตรวจอายุรกรรมทั่วไป...",
    img: "https://w2.med.cmu.ac.th/opd/wp-content/uploads/2020/02/IMG_2237-1024x768.jpg?x48287",
  },
  {
    id: "ID#QR-003",
    x: 70,
    y: 78,
    text: "หน่วยตรวจฉุกเฉิน...",
    img: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR4w275GUDXN-foFddPGxExUMD9xzNLNzVpKw&s",
  },
];

const shapeBelongsToFloor = (shape, floorId) => {
  const shapeFloorId = shape.floorId ?? shape.floor_id ?? shape.floor?.id;
  return shapeFloorId == null || String(shapeFloorId) === String(floorId);
};

export default function AddDestination() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const floorId = searchParams.get("floorId");
  const buildingId = searchParams.get("buildingId");

  const [floorOptions, setFloorOptions] = useState([]);
  const [floorData, setFloorData] = useState(null);
  const [shapes, setShapes] = useState([]);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const [activeTool, setActiveTool] = useState("mouse");
  const [activePoint, setActivePoint] = useState(null);
  const [serverPoints, setServerPoints] = useState(null);
  const [form, setForm] = useState({
    name: "",
    description: "",
    category: "",
    x: "",
    y: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [names, setNames] = useState([""]);

  useEffect(() => {
    if (!imageFile) {
      setImagePreview(null);
      return;
    }
    const objectUrl = URL.createObjectURL(imageFile);
    setImagePreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [imageFile]);

  const points = serverPoints || defaultPoints;

  // Load floors of the building
  useEffect(() => {
    if (!buildingId) return;
    FloorAPI.listByBuilding(buildingId)
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.items || [];
        setFloorOptions(list);
        if (!floorId && list.length > 0) {
          setSearchParams(
            { buildingId, floorId: String(list[0].id) },
            { replace: true }
          );
        }
      })
      .catch(() => { });
  }, [buildingId, floorId, setSearchParams]);

  // Load active floor detail (for map image)
  useEffect(() => {
    if (!floorId) {
      setFloorData(null);
      return;
    }
    NavigationAPI.getFloor(floorId)
      .then((data) => setFloorData(data))
      .catch(() => setFloorData(null));
  }, [floorId]);

  // Load shapes context (rooms)
  useEffect(() => {
    if (!floorId) {
      setShapes([]);
      return;
    }
    setShapes([]);
    ShapeAPI.listByFloor(floorId)
      .then((d) =>
        setShapes(
          (Array.isArray(d) ? d : []).filter((shape) =>
            shapeBelongsToFloor(shape, floorId)
          )
        )
      )
      .catch(() => setShapes([]));
  }, [floorId]);

  const reload = () => {
    if (!floorId) return;
    DestinationAPI.listByFloor(floorId)
      .then((data) => setServerPoints(Array.isArray(data) ? data : []))
      .catch(() => { });
  };

  useEffect(reload, [floorId]);

  useEffect(() => {
    if (!activePoint) return;
    const p = points.find((x) => x.id === activePoint);
    if (p) {
      const rawName = p.name || p.text || "";
      const parsed = parseNames(rawName);
      setNames(parsed.all.length > 0 ? parsed.all : [""]);
      setForm({
        name: rawName,
        description: p.description || "",
        category: p.category || "",
        x: String(p.x ?? ""),
        y: String(p.y ?? ""),
      });
      setImageFile(null); // Clear selected file when choosing another existing point
    }
  }, [activePoint, points]);

  const onSave = async () => {
    setErrMsg("");
    if (!floorId) {
      setErrMsg(t("admin.addDestination.noFloorId", "ไม่พบ floorId ใน URL"));
      return;
    }
    setSaving(true);
    try {
      const combinedName = names.map(n => n.trim()).filter(Boolean).join(", ");
      const payload = {
        name: combinedName,
        description: form.description,
        category: form.category,
        x: Number(form.x) || 0,
        y: Number(form.y) || 0,
        image: imageFile || undefined,
      };
      if (activePoint) {
        await DestinationAPI.update(activePoint, payload);
      } else {
        await DestinationAPI.create(floorId, payload);
      }
      setForm({ name: "", description: "", category: "", x: "", y: "" });
      setNames([""]);
      setImageFile(null);
      setActivePoint(null);
      reload();
    } catch (e) {
      setErrMsg(e.message || t("admin.addDestination.saveFailed", "บันทึกไม่สำเร็จ"));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!activePoint) return;
    setShowConfirmDelete(false);
    try {
      await DestinationAPI.remove(activePoint);
      setActivePoint(null);
      setForm({ name: "", description: "", category: "", x: "", y: "" });
      setNames([""]);
      reload();
    } catch (e) {
      alert(e.message || t("admin.addDestination.deleteFailed", "ลบไม่สำเร็จ"));
    }
  };

  const handleFloorClick = (fId) => {
    setSearchParams({ buildingId, floorId: String(fId) });
    setActivePoint(null);
    setForm({ name: "", description: "", category: "", x: "", y: "" });
    setNames([""]);
    setImageFile(null);
  };

  const getUploadPreviewUrl = () => {
    if (imagePreview) {
      return imagePreview;
    }
    if (activePoint) {
      const p = points.find((x) => x.id === activePoint);
      if (p) {
        return resolveAsset(p.img || p.imageUrl);
      }
    }
    return "https://w2.med.cmu.ac.th/opd/wp-content/uploads/2020/02/IMG_2237-1024x768.jpg?x48287";
  };

  const tools = [
    { id: "mouse", icon: ClickIcon },
    { id: "hand", icon: HandIcon },
    { id: "eye", icon: EyeIcon },
    { id: "home", icon: HomeIcon },
  ];

  const getToolTitle = (id) => {
    if (id === "mouse") return t("admin.addDestination.toolSelectEdit", "เลือก/แก้ไข");
    if (id === "hand") return t("admin.addDestination.toolPanDrag", "เลื่อน/ลาก");
    if (id === "eye") return t("admin.addDestination.toolView", "ดูแผนที่");
    if (id === "home") return t("admin.addDestination.toolPlaceRoom", "ปักหมุดห้อง");
    return "";
  };

  // Modify points array for live coordinate movements or new draft pin
  const displayedPoints = points.map((p) => {
    if (activePoint && p.id === activePoint) {
      const combinedName = names.map(n => n.trim()).filter(Boolean).join(", ");
      return {
        ...p,
        x: form.x !== "" ? Number(form.x) : p.x,
        y: form.y !== "" ? Number(form.y) : p.y,
        name: combinedName || p.name || p.text,
        text: combinedName || p.name || p.text,
      };
    }
    return p;
  });

  if (!activePoint && form.x !== "" && form.y !== "") {
    const combinedName = names.map(n => n.trim()).filter(Boolean).join(", ");
    displayedPoints.push({
      id: "draft",
      x: Number(form.x),
      y: Number(form.y),
      name: combinedName || t("admin.addDestination.roomDraft", "ห้องใหม่ (ดราฟต์)"),
      text: combinedName || t("admin.addDestination.roomDraft", "ห้องใหม่ (ดราฟต์)"),
      imageUrl: imagePreview || "https://w2.med.cmu.ac.th/opd/wp-content/uploads/2020/02/IMG_2237-1024x768.jpg?x48287",
      isDraft: true,
    });
  }

  const onMapClick = (px) => {
    if (activeTool !== "home") return;
    setActivePoint(null);
    setForm((f) => ({
      ...f,
      x: String(Math.round(px.x)),
      y: String(Math.round(px.y)),
    }));
  };

  const markers = useMemo(() => {
    return displayedPoints.map((p) => ({
      id: p.id,
      type: "poi",
      x: Number(p.x) || 0,
      y: Number(p.y) || 0,
      label: parseNames(p.name || p.text).primary || (p.id === "draft" ? t("admin.addDestination.roomDraft", "ห้องใหม่ (ดราฟต์)") : ""),
      onClick: () => {
        if (p.id !== "draft") {
          setActivePoint((prev) => (String(prev) === String(p.id) ? null : p.id));
        }
      },
    }));
  }, [displayedPoints, t]);

  const bgImage = resolveAsset(floorData?.imageUrl);

  return (
    <div className="font-sans w-full h-full overflow-hidden">
      <div className="flex w-full h-full overflow-hidden">
        {/* LEFT: 70% map area */}
        <div className="w-[70%] min-w-0 flex flex-col">
          {/* Top bar: floor menu + tools */}
          <div className="flex items-center justify-between bg-white ml-4 shrink-0">
            {/* Floor selector buttons */}
            <div className="flex items-center">
              {floorOptions.map((f) => (
                <button
                  key={f.id}
                  onClick={() => handleFloorClick(f.id)}
                  className={`px-6 h-10 rounded-lg m-1 text-sm font-medium transition ${String(f.id) === String(floorId)
                    ? "bg-blue-600 text-white"
                    : "bg-white text-slate-500 hover:bg-gray-200"
                    }`}
                >
                  {getFloorLabel(f, t)}
                </button>
              ))}
              {floorOptions.length === 0 && (
                <div className="text-slate-400 text-xs px-4">{t("admin.addDestination.loadingFloors", "กำลังโหลดชั้น...")}</div>
              )}
            </div>

            {/* Map tool buttons */}
            <div className="px-2 py-1">
              <div className="flex items-center justify-center">
                {tools.map((tool) => {
                  const Icon = tool.icon;
                  return (
                    <button
                      key={tool.id}
                      title={getToolTitle(tool.id)}
                      onClick={() => setActiveTool(tool.id)}
                      className={`relative flex flex-1 items-center justify-center px-3 py-3 m-1 rounded-lg transition ${activeTool === tool.id
                        ? "bg-blue-600 text-white"
                        : "text-slate-600 hover:bg-gray-200"
                        }`}
                    >
                      <Icon className="w-4 h-4" />
                    </button>
                  );
                })}

                <button className="flex w-12 h-12 m-1 font-medium rounded-lg items-center justify-center text-slate-700 hover:bg-gray-200">
                  <PlusIcon className="w-3 h-3" />
                </button>

                <div className="px-3 text-slate-700 font-medium">100%</div>

                <button className="flex w-12 h-12 m-1 rounded-lg items-center justify-center text-slate-700 hover:bg-gray-200">
                  {/* Minus Icon */}
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3"><line x1="5" y1="12" x2="19" y2="12" /></svg>
                </button>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 shrink-0"></div>

          {/* Map canvas with LeafletFloorMap */}
          <div className="relative flex-1 min-h-[500px] bg-slate-200">
            {bgImage ? (
              <LeafletFloorMap
                imageUrl={bgImage}
                imageWidth={floorData?.imageWidth}
                imageHeight={floorData?.imageHeight}
                shapes={shapes}
                markers={markers}
                onMapClick={onMapClick}
                className="w-full h-full"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
                {floorId ? t("admin.addDestination.floorMapEmpty", "ไม่มีรูปแผนผังของชั้นนี้") : t("admin.addDestination.selectFloorPrompt", "กรุณาเลือกชั้น")}
              </div>
            )}

            {/* Coordinate display in bottom-right corner */}
            <div className="absolute bottom-4 right-4 bg-slate-900/85 text-white font-mono text-xs px-3 py-1.5 rounded shadow-lg backdrop-blur-sm z-50">
              {activePoint ? (
                (() => {
                  const p = points.find((x) => x.id === activePoint);
                  return p ? `X: ${Math.round(p.x)}, Y: ${Math.round(p.y)}` : "X: 0, Y: 0";
                })()
              ) : form.x && form.y ? (
                `X: ${Math.round(Number(form.x))}, Y: ${Math.round(Number(form.y))}`
              ) : (
                "X: 0, Y: 0"
              )}
            </div>
          </div>
        </div>

        {/* RIGHT: 30% form area */}
        <div className="w-[30%] min-w-[320px] h-full flex flex-col border-l border-gray-200 bg-white">
          {/* Form content */}
          <div className="flex-1 min-h-0 overflow-y-auto p-4">
            <div className="font-semibold text-[17px] text-slate-900 border-b border-slate-200 pb-4">
              {t("admin.addDestination.formTitle", "แบบฟอร์มตั้งค่าห้อง/สถานที่ปลายทาง")}
              {activePoint && (
                <span className="text-yellow-500 m-2 ">{t("admin.addDestination.editActiveId", { defaultValue: "(แก้ไข ID: {{id}})", id: activePoint })}</span>
              )}
            </div>

            {/* Photo upload section */}
            <div className="px-1 py-2"></div>
            <p className="text-sm text-slate-900 mt-0 m-1 font-semibold">
              {t("admin.addDestination.imageFileLabel", "ไฟล์รูปภาพ")} <span className="text-red-500">*</span>
            </p>
            <label
              className="flex flex-col items-center justify-center rounded-md cursor-pointer px-7 py-7 m-2 bg-cover bg-center min-h-[150px] transition hover:opacity-95"
              style={{
                backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url('${getUploadPreviewUrl()}')`,
              }}
            >
              <UploadIcon className="text-white w-6 h-6" />
              <p className="text-sm mt-2 font-medium text-blue-300">
                {t("admin.addDestination.addImageUpload", "เพิ่มรูปภาพที่ต้องการอัปโหลด")}
              </p>
              <p className="text-xs text-slate-300">JPEG, PNG</p>
              {/* Hidden file input */}
              <input
                type="file"
                accept=".jpg,.jpeg,.png"
                className="hidden"
                onChange={(e) => setImageFile(e.target.files?.[0] || null)}
              />
              {imageFile && (
                <p className="text-xs text-emerald-400 mt-2 bg-slate-900/60 px-2 py-0.5 rounded truncate max-w-full">
                  {imageFile.name}
                </p>
              )}
            </label>

            {/* Room name + category fields */}
            <div className="px-2 py-2">
              <label className="text-sm mt-3 block mb-1 font-semibold text-slate-700">
                {t("admin.addDestination.roomNameLabel", "ชื่อห้อง")} <span className="text-red-500">*</span>
              </label>
              
              {names.map((name, index) => (
                <div key={index} className="flex items-center gap-2 mb-2">
                  <input
                    type="text"
                    placeholder={
                      index === 0
                        ? t("admin.addDestination.roomNamePlaceholder", "ประชาสัมพันธ์")
                        : `${t("admin.addDestination.roomAliasPlaceholder", "ชื่ออื่น ๆ")} ${index}`
                    }
                    value={name}
                    onChange={(e) => {
                      const newNames = [...names];
                      newNames[index] = e.target.value;
                      setNames(newNames);
                    }}
                    className="w-full border border-slate-300 rounded-md p-3 text-sm h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {index > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        const newNames = names.filter((_, i) => i !== index);
                        setNames(newNames);
                      }}
                      className="px-3 h-10 text-red-500 hover:bg-red-50 rounded-md border border-red-200 text-xs font-semibold shrink-0 transition"
                    >
                      {t("common.delete", "ลบ")}
                    </button>
                  )}
                </div>
              ))}

              <button
                type="button"
                onClick={() => setNames([...names, ""])}
                className="mt-1 px-4 py-1.5 border border-dashed border-blue-300 text-blue-600 hover:bg-blue-50 rounded-lg text-xs font-bold transition flex items-center gap-1 mb-3"
              >
                <span>+</span> {t("admin.addDestination.addAliasBtn", "เพิ่มชื่ออื่น")}
              </button>

              <label className="text-sm mt-4 block mb-1 font-semibold text-slate-700">
                {t("admin.addDestination.categoryLabel", "หมวดหมู่")}
              </label>
              <input
                type="text"
                placeholder={t("admin.addDestination.categoryPlaceholder", "เช่น ห้องตรวจ, ประชาสัมพันธ์")}
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full border border-slate-300 rounded-md p-3 text-sm h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="px-2 py-2">
              {/* Description textarea */}
              <label className="text-sm mt-3 mb-1 block font-semibold text-slate-700">
                {t("admin.addDestination.additionalDescLabel", "คำอธิบายเพิ่มเติม")}
              </label>
              <textarea
                placeholder="-"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full border border-slate-300 rounded-md p-3 text-sm h-24 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Map coordinate inputs */}
            <label className="flex text-sm px-1 mt-4 mb-1 font-semibold text-slate-700">
              <LocationIcon className="text-blue-700 w-4 h-4 mt-0.5 mr-2" />{" "}
              {t("admin.addDestination.coordinatesLabel", "พิกัดบนแผนที่ (%)")}
            </label>
            <div className="flex px-1 py-0 mb-4">
              <input
                type="text"
                placeholder="x-coord"
                value={form.x}
                onChange={(e) => setForm({ ...form, x: e.target.value })}
                className="w-full border border-slate-300 rounded-md p-3 text-sm h-10 mr-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                placeholder="y-coord"
                value={form.y}
                onChange={(e) => setForm({ ...form, y: e.target.value })}
                className="w-full border border-slate-300 rounded-md p-3 text-sm h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="px-2 mb-[40px]">
              <button
                onClick={() => setShowConfirmDelete(true)}
                disabled={!activePoint}
                className="w-full text-sm border border-red-100 bg-red-50 text-red-700 rounded-lg p-2 font-semibold disabled:opacity-50 hover:bg-red-100 transition"
              >
                {t("admin.addDestination.deleteLocation", "ลบสถานที่")}
              </button>
              {errMsg && (
                <div className="mt-2 text-sm text-red-600 font-semibold">{errMsg}</div>
              )}
            </div>
          </div>

          {/* Footer action buttons */}
          <div className="shrink-0 border-t border-slate-100 p-3 bg-slate-50">
            <div className="flex justify-center gap-2">
              <button
                onClick={() => {
                  setActivePoint(null);
                  setForm({ name: "", description: "", category: "", x: "", y: "" });
                  setNames([""]);
                  setImageFile(null);
                }}
                className="flex-1 py-2 bg-white text-slate-700 rounded-lg border border-gray-200 font-medium hover:bg-gray-50 transition"
              >
                {t("admin.addDestination.cancel", "ยกเลิก")}
              </button>
              <button
                onClick={onSave}
                disabled={saving}
                className="flex-1 py-2 bg-yellow-500 text-white rounded-lg font-medium disabled:opacity-60 hover:bg-yellow-600 transition"
              >
                {saving ? t("admin.addDestination.saving", "กำลังบันทึก...") : activePoint ? t("admin.addDestination.edit", "แก้ไข") : t("admin.addDestination.add", "เพิ่ม")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showConfirmDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full border border-slate-100">
            <h3 className="text-lg font-bold text-slate-900 mb-2">{t("admin.addDestination.confirmDeleteTitle", "ยืนยันการลบ")}</h3>
            <p className="text-sm text-slate-600 mb-6">{t("admin.addDestination.confirmDeleteDesc", "คุณแน่ใจหรือไม่ว่าต้องการลบห้อง/สถานที่ปลายทางนี้?")}</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirmDelete(false)}
                className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 font-semibold hover:bg-slate-50 transition"
              >
                {t("admin.addDestination.cancel", "ยกเลิก")}
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition"
              >
                {t("admin.addDestination.confirmDeleteTitle", "ยืนยันการลบ")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
